<template>
  <main class="mall_main w-full">
    <div class="pt-5 flex flex-col h-full">
      <div class="w-fit flex justify-center items-center bg-slate-100 rounded-full mx-auto px-4 py-1 text-sm sticky top-1 z-10">
        {{ currentDate }}
      </div>

      <!-- 스크롤 가능한 채팅 영역 -->
      <div ref="chatContainer" class="flex-1 overflow-y-auto w-full my-5 p-5">
        <!-- 초기 인사 메시지 -->
        <div class="mb-6">
          <svg width="51" height="51" viewBox="0 0 51 51" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M25.4958 0.5C21.1585 0.498539 16.8954 1.62552 13.1255 3.7702C9.35555 5.91488 6.20845 9.00347 3.99339 12.7325C1.77834 16.4615 0.571559 20.7027 0.491621 25.0392C0.411682 29.3758 1.46134 33.6585 3.53745 37.4667L0.579124 47.8417C0.475029 48.206 0.472256 48.5918 0.571103 48.9575C0.66995 49.3233 0.866674 49.6552 1.1401 49.9175C1.41353 50.1798 1.75331 50.3625 2.12287 50.4461C2.49244 50.5296 2.87779 50.5108 3.23745 50.3917L12.9791 47.1458C16.2994 49.0645 20.0165 50.1936 23.8429 50.4458C27.6693 50.6981 31.5025 50.0667 35.0459 48.6005C38.5893 47.1344 41.7479 44.8727 44.2774 41.9905C46.8068 39.1083 48.6394 35.6829 49.6332 31.9791C50.627 28.2754 50.7555 24.3927 50.0087 20.6314C49.2619 16.8701 47.6599 13.331 45.3265 10.2878C42.9931 7.24472 39.9909 4.77918 36.5522 3.08195C33.1135 1.38471 29.3305 0.501281 25.4958 0.5Z"
              fill="url(#paint0_linear_54_4)"
            />
            <path
              d="M17.1624 21.3333C17.1624 20.7808 17.3819 20.2509 17.7726 19.8602C18.1633 19.4695 18.6932 19.25 19.2458 19.25H31.7458C32.2983 19.25 32.8282 19.4695 33.2189 19.8602C33.6096 20.2509 33.8291 20.7808 33.8291 21.3333C33.8291 21.8859 33.6096 22.4158 33.2189 22.8065C32.8282 23.1972 32.2983 23.4167 31.7458 23.4167H19.2458C18.6932 23.4167 18.1633 23.1972 17.7726 22.8065C17.3819 22.4158 17.1624 21.8859 17.1624 21.3333ZM19.2458 27.5833H27.5791C28.1316 27.5833 28.6615 27.8028 29.0522 28.1935C29.4429 28.5842 29.6624 29.1141 29.6624 29.6667C29.6624 30.2192 29.4429 30.7491 29.0522 31.1398C28.6615 31.5305 28.1316 31.75 27.5791 31.75H19.2458C18.6932 31.75 18.1633 31.5305 17.7726 31.1398C17.3819 30.7491 17.1624 30.2192 17.1624 29.6667C17.1624 29.1141 17.3819 28.5842 17.7726 28.1935C18.1633 27.8028 18.6932 27.5833 19.2458 27.5833Z"
              fill="url(#paint1_linear_54_4)"
            />
            <defs>
              <linearGradient id="paint0_linear_54_4" x1="2.28329" y1="9.875" x2="45.9333" y2="84.2958" gradientUnits="userSpaceOnUse">
                <stop stop-color="#0FAFFF" />
                <stop offset="1" stop-color="#CC23D1" />
              </linearGradient>
              <linearGradient id="paint1_linear_54_4" x1="18.6208" y1="19.4708" x2="20.1958" y2="33.0042" gradientUnits="userSpaceOnUse">
                <stop stop-color="#FDFDFD" />
                <stop offset="1" stop-color="#CCEAFF" />
              </linearGradient>
            </defs>
          </svg>
          <p class="text-xl font-medium mt-3">안녕하세요.<br />총회교육개발원 콘텐츠 몰 챗봇입니다.<br />무엇을 도와드릴까요?</p>
          <p class="text-sm text-neutral-400 mt-2">{{ currentTime }}</p>
        </div>

        <!-- 질문 예시 grid - 대화가 없을 때만 표시 -->
        <div v-if="messages.length === 0" class="grid grid-cols-2 gap-3 my-5">
          <div class="w-full" v-for="question in displayedQuestions" :key="question">
            <div class="w-full h-full border border-neutral-200 rounded-lg p-3 cursor-pointer hover:bg-gray-50 transition-colors text-sm" @click="handleQuestionClick(question)">
              {{ question }}
            </div>
          </div>
        </div>

        <!-- 대화 히스토리 -->
        <div v-for="(message, index) in messages" :key="index" class="my-4">
          <!-- 사용자 메시지 -->
          <div v-if="message.type === 'user'" class="flex justify-end">
            <div class="max-w-[calc(100%-60px)]">
              <p class="text-blue-700 bg-blue-50 rounded-lg p-4 break-words">
                {{ message.content }}
              </p>
              <p class="text-sm text-neutral-400 mt-2 text-right">{{ message.time }}</p>
            </div>
          </div>

          <!-- 봇 메시지 -->
          <div v-else class="flex justify-start">
            <div class="max-w-[calc(100%-60px)]">
              <div class="flex items-start gap-3">
                <div class="flex-shrink-0">
                  <svg width="32" height="32" viewBox="0 0 51 51" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M25.4958 0.5C21.1585 0.498539 16.8954 1.62552 13.1255 3.7702C9.35555 5.91488 6.20845 9.00347 3.99339 12.7325C1.77834 16.4615 0.571559 20.7027 0.491621 25.0392C0.411682 29.3758 1.46134 33.6585 3.53745 37.4667L0.579124 47.8417C0.475029 48.206 0.472256 48.5918 0.571103 48.9575C0.66995 49.3233 0.866674 49.6552 1.1401 49.9175C1.41353 50.1798 1.75331 50.3625 2.12287 50.4461C2.49244 50.5296 2.87779 50.5108 3.23745 50.3917L12.9791 47.1458C16.2994 49.0645 20.0165 50.1936 23.8429 50.4458C27.6693 50.6981 31.5025 50.0667 35.0459 48.6005C38.5893 47.1344 41.7479 44.8727 44.2774 41.9905C46.8068 39.1083 48.6394 35.6829 49.6332 31.9791C50.627 28.2754 50.7555 24.3927 50.0087 20.6314C49.2619 16.8701 47.6599 13.331 45.3265 10.2878C42.9931 7.24472 39.9909 4.77918 36.5522 3.08195C33.1135 1.38471 29.3305 0.501281 25.4958 0.5Z"
                      fill="url(#paint0_linear_bot)"
                    />
                    <path
                      d="M17.1624 21.3333C17.1624 20.7808 17.3819 20.2509 17.7726 19.8602C18.1633 19.4695 18.6932 19.25 19.2458 19.25H31.7458C32.2983 19.25 32.8282 19.4695 33.2189 19.8602C33.6096 20.2509 33.8291 20.7808 33.8291 21.3333C33.8291 21.8859 33.6096 22.4158 33.2189 22.8065C32.8282 23.1972 32.2983 23.4167 31.7458 23.4167H19.2458C18.6932 23.4167 18.1633 23.1972 17.7726 22.8065C17.3819 22.4158 17.1624 21.8859 17.1624 21.3333ZM19.2458 27.5833H27.5791C28.1316 27.5833 28.6615 27.8028 29.0522 28.1935C29.4429 28.5842 29.6624 29.1141 29.6624 29.6667C29.6624 30.2192 29.4429 30.7491 29.0522 31.1398C28.6615 31.5305 28.1316 31.75 27.5791 31.75H19.2458C18.6932 31.75 18.1633 31.5305 17.7726 31.1398C17.3819 30.7491 17.1624 30.2192 17.1624 29.6667C17.1624 29.1141 17.3819 28.5842 17.7726 28.1935C18.1633 27.8028 18.6932 27.5833 19.2458 27.5833Z"
                      fill="url(#paint1_linear_bot)"
                    />
                    <defs>
                      <linearGradient id="paint0_linear_bot" x1="2.28329" y1="9.875" x2="45.9333" y2="84.2958" gradientUnits="userSpaceOnUse">
                        <stop stop-color="#0FAFFF" />
                        <stop offset="1" stop-color="#CC23D1" />
                      </linearGradient>
                      <linearGradient id="paint1_linear_bot" x1="18.6208" y1="19.4708" x2="20.1958" y2="33.0042" gradientUnits="userSpaceOnUse">
                        <stop stop-color="#FDFDFD" />
                        <stop offset="1" stop-color="#CCEAFF" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
                <div>
                  <p class="bg-neutral-100 rounded-lg p-4 break-words">
                    {{ message.content }}
                  </p>
                  <p class="text-sm text-neutral-400 mt-2">{{ message.time }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 로딩 인디케이터 -->
        <div v-if="isLoading" class="my-4 flex justify-start">
          <div class="max-w-[calc(100%-60px)]">
            <div class="flex items-start gap-3">
              <div class="flex-shrink-0">
                <svg width="32" height="32" viewBox="0 0 51 51" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M25.4958 0.5C21.1585 0.498539 16.8954 1.62552 13.1255 3.7702C9.35555 5.91488 6.20845 9.00347 3.99339 12.7325C1.77834 16.4615 0.571559 20.7027 0.491621 25.0392C0.411682 29.3758 1.46134 33.6585 3.53745 37.4667L0.579124 47.8417C0.475029 48.206 0.472256 48.5918 0.571103 48.9575C0.66995 49.3233 0.866674 49.6552 1.1401 49.9175C1.41353 50.1798 1.75331 50.3625 2.12287 50.4461C2.49244 50.5296 2.87779 50.5108 3.23745 50.3917L12.9791 47.1458C16.2994 49.0645 20.0165 50.1936 23.8429 50.4458C27.6693 50.6981 31.5025 50.0667 35.0459 48.6005C38.5893 47.1344 41.7479 44.8727 44.2774 41.9905C46.8068 39.1083 48.6394 35.6829 49.6332 31.9791C50.627 28.2754 50.7555 24.3927 50.0087 20.6314C49.2619 16.8701 47.6599 13.331 45.3265 10.2878C42.9931 7.24472 39.9909 4.77918 36.5522 3.08195C33.1135 1.38471 29.3305 0.501281 25.4958 0.5Z"
                    fill="url(#paint0_linear_loading)"
                  />
                  <path
                    d="M17.1624 21.3333C17.1624 20.7808 17.3819 20.2509 17.7726 19.8602C18.1633 19.4695 18.6932 19.25 19.2458 19.25H31.7458C32.2983 19.25 32.8282 19.4695 33.2189 19.8602C33.6096 20.2509 33.8291 20.7808 33.8291 21.3333C33.8291 21.8859 33.6096 22.4158 33.2189 22.8065C32.8282 23.1972 32.2983 23.4167 31.7458 23.4167H19.2458C18.6932 23.4167 18.1633 23.1972 17.7726 22.8065C17.3819 22.4158 17.1624 21.8859 17.1624 21.3333ZM19.2458 27.5833H27.5791C28.1316 27.5833 28.6615 27.8028 29.0522 28.1935C29.4429 28.5842 29.6624 29.1141 29.6624 29.6667C29.6624 30.2192 29.4429 30.7491 29.0522 31.1398C28.6615 31.5305 28.1316 31.75 27.5791 31.75H19.2458C18.6932 31.75 18.1633 31.5305 17.7726 31.1398C17.3819 30.7491 17.1624 30.2192 17.1624 29.6667C17.1624 29.1141 17.3819 28.5842 17.7726 28.1935C18.1633 27.8028 18.6932 27.5833 19.2458 27.5833Z"
                    fill="url(#paint1_linear_loading)"
                  />
                  <defs>
                    <linearGradient id="paint0_linear_loading" x1="2.28329" y1="9.875" x2="45.9333" y2="84.2958" gradientUnits="userSpaceOnUse">
                      <stop stop-color="#0FAFFF" />
                      <stop offset="1" stop-color="#CC23D1" />
                    </linearGradient>
                    <linearGradient id="paint1_linear_loading" x1="18.6208" y1="19.4708" x2="20.1958" y2="33.0042" gradientUnits="userSpaceOnUse">
                      <stop stop-color="#FDFDFD" />
                      <stop offset="1" stop-color="#CCEAFF" />
                    </linearGradient>
                  </defs>
                </svg>
              </div>
              <div class="bg-neutral-100 rounded-lg p-4">
                <div class="flex items-center gap-1">
                  <div class="w-2 h-2 bg-neutral-400 rounded-full animate-bounce" style="animation-delay: 0ms"></div>
                  <div class="w-2 h-2 bg-neutral-400 rounded-full animate-bounce" style="animation-delay: 150ms"></div>
                  <div class="w-2 h-2 bg-neutral-400 rounded-full animate-bounce" style="animation-delay: 300ms"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 입력 영역 -->
      <div class="sticky bottom-0 w-full bg-white p-5 border-t border-neutral-100">
        <div class="w-full flex justify-between items-center gap-3 border border-neutral-200 rounded-lg px-4 py-3">
          <input v-model="userInput" type="text" maxlength="500" placeholder="궁금한 사항을 입력해 주세요" class="w-full focus:outline-none" @keyup.enter="handleSendMessage" :disabled="isLoading" />
          <button @click="handleSendMessage" :disabled="!userInput.trim() || isLoading">
            <svg v-if="!isLoading" width="31" height="31" viewBox="0 0 31 31" fill="none" xmlns="http://www.w3.org/2000/svg" :class="{ 'opacity-50': !userInput.trim() }">
              <path d="M17.476 17.8036L2.88058 22.1794L4.80064 15.7507L2.88058 9.32196L17.476 13.6978C19.5117 14.3085 19.5117 17.1907 17.476 17.8036Z" fill="url(#paint0_radial_55_8)" />
              <path
                d="M2.30201 0.863873C2.12265 0.774165 1.92171 0.736596 1.72205 0.755439C1.52239 0.774281 1.33203 0.84878 1.17262 0.97046C1.01321 1.09214 0.891145 1.25612 0.820317 1.44374C0.749488 1.63136 0.732737 1.83509 0.771969 2.03176L3.77848 12.4249C3.83454 12.6186 3.94411 12.7925 4.0946 12.9267C4.2451 13.0609 4.4304 13.1499 4.62921 13.1835L16.8224 15.2257C17.3967 15.3393 17.3967 16.1621 16.8224 16.2757L4.62921 18.3179C4.4304 18.3515 4.2451 18.4405 4.0946 18.5747C3.94411 18.7089 3.83454 18.8828 3.77848 19.0765L0.771969 29.4696C0.732737 29.6663 0.749488 29.87 0.820317 30.0577C0.891145 30.2453 1.01321 30.4093 1.17262 30.5309C1.33203 30.6526 1.52239 30.7271 1.72205 30.746C1.92171 30.7648 2.12265 30.7272 2.30201 30.6375L30.1599 16.7086C30.3376 16.6195 30.4871 16.4827 30.5915 16.3136C30.696 16.1444 30.7513 15.9495 30.7513 15.7507C30.7513 15.5519 30.696 15.357 30.5915 15.1878C30.4871 15.0187 30.3376 14.8819 30.1599 14.7928L2.30201 0.863873Z"
                fill="url(#paint1_linear_55_8)"
              />
              <path
                d="M2.30201 0.863873C2.12265 0.774165 1.92171 0.736596 1.72205 0.755439C1.52239 0.774281 1.33203 0.84878 1.17262 0.97046C1.01321 1.09214 0.891145 1.25612 0.820317 1.44374C0.749488 1.63136 0.732737 1.83509 0.771969 2.03176L3.77848 12.4249C3.83454 12.6186 3.94411 12.7925 4.0946 12.9267C4.2451 13.0609 4.4304 13.1499 4.62921 13.1835L16.8224 15.2257C17.3967 15.3393 17.3967 16.1621 16.8224 16.2757L4.62921 18.3179C4.4304 18.3515 4.2451 18.4405 4.0946 18.5747C3.94411 18.7089 3.83454 18.8828 3.77848 19.0765L0.771969 29.4696C0.732737 29.6663 0.749488 29.87 0.820317 30.0577C0.891145 30.2453 1.01321 30.4093 1.17262 30.5309C1.33203 30.6526 1.52239 30.7271 1.72205 30.746C1.92171 30.7648 2.12265 30.7272 2.30201 30.6375L30.1599 16.7086C30.3376 16.6195 30.4871 16.4827 30.5915 16.3136C30.696 16.1444 30.7513 15.9495 30.7513 15.7507C30.7513 15.5519 30.696 15.357 30.5915 15.1878C30.4871 15.0187 30.3376 14.8819 30.1599 14.7928L2.30201 0.863873Z"
                fill="url(#paint2_linear_55_8)"
              />
              <defs>
                <radialGradient id="paint0_radial_55_8" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(-0.32093 15.7507) scale(15.9391 2.40776)">
                  <stop stop-color="#0094F0" />
                  <stop offset="1" stop-color="#2052CB" />
                </radialGradient>
                <linearGradient id="paint1_linear_55_8" x1="0.75054" y1="-11.4386" x2="26.4441" y2="22.7023" gradientUnits="userSpaceOnUse">
                  <stop stop-color="#3BD5FF" />
                  <stop offset="1" stop-color="#0094F0" />
                </linearGradient>
                <linearGradient id="paint2_linear_55_8" x1="15.7509" y1="8.83551" x2="23.5533" y2="29.9454" gradientUnits="userSpaceOnUse">
                  <stop offset="0.125" stop-color="#DCF8FF" stop-opacity="0" />
                  <stop offset="0.769" stop-color="#FF6CE8" stop-opacity="0.7" />
                </linearGradient>
              </defs>
            </svg>
            <svg v-else class="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </button>
        </div>
      </div>
    </div>
  </main>
</template>

<script setup>
import { ref, onMounted, nextTick } from "vue";
import axios from "axios";

// 반응형 데이터 정의
const userInput = ref("");
const isLoading = ref(false);
const chatContainer = ref(null);

// 대화 히스토리 배열
const messages = ref([]);

// 오늘 날짜를 포맷팅하는 함수
const getFormattedDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");

  const dayNames = ["일", "월", "화", "수", "목", "금", "토"];
  const dayName = dayNames[today.getDay()];

  return `${year}. ${month}. ${day} (${dayName})`;
};

// 현재 시간을 포맷팅하는 함수
const getFormattedTime = () => {
  const now = new Date();
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");

  return `${hours}:${minutes}`;
};

// 현재 날짜
const currentDate = ref(getFormattedDate());

// 현재 시간
const currentTime = ref(getFormattedTime());

const searchKeywords = ref([]);
const searchContentType = ref(null);

// 스크롤을 맨 아래로 이동
const scrollToBottom = async () => {
  await nextTick();
  if (chatContainer.value) {
    chatContainer.value.scrollTop = chatContainer.value.scrollHeight;
  }
};

// 메시지 추가 함수
const addMessage = (type, content) => {
  messages.value.push({
    type,
    content,
    time: getFormattedTime(),
  });
  scrollToBottom();
};

// 메시지 전송 함수
const handleSendMessage = () => {
  if (isLoading.value) return;
  getSearchKeywords();
};

const firstPrompt = ref("");
const requiredPerson = ref(null);
const contentType = ref(null);
const group = ref(null);

const getSearchKeywords = () => {
  if (userInput.value.trim()) {
    isLoading.value = true;
    const searchQuery = userInput.value;

    // 사용자 메시지 추가
    addMessage("user", searchQuery);
    userInput.value = ""; // 입력 필드 초기화

    // API 파라미터 구성
    const params = new URLSearchParams();
    params.append("naturalLanguage", searchQuery);
    if (firstPrompt.value) params.append("firstPrompt", firstPrompt.value);
    if (requiredPerson.value != null) params.append("requiredPerson", requiredPerson.value);
    if (contentType.value != null) params.append("contentType", contentType.value);
    if (group.value != null) params.append("group", group.value);

    axios
      .get("/new_api/open/item/nl/v1/chat?" + params.toString())
      .then((response) => {
        const data = response.data;
        if (data.code == 200) {
          const resData = data.data;

          // 응답에서 누적 파라미터 업데이트
          if (resData.requiredPerson != null) requiredPerson.value = resData.requiredPerson;
          if (resData.contentType != null) contentType.value = resData.contentType;
          if (resData.group != null) group.value = resData.group;

          // 필수값(group, requiredPerson) 확보 여부 확인
          const hasRequiredFields = requiredPerson.value != null && group.value != null;

          // 대화가 완료되고 필수값이 모두 있는 경우 결과 페이지로 이동
          if (resData.finished && hasRequiredFields && resData.keywords && resData.keywords.length > 0) {
            searchKeywords.value = resData.keywords;
            searchContentType.value = resData.contentType;

            // localStorage에 데이터 저장
            localStorage.setItem("searchKeywords", JSON.stringify(searchKeywords.value));
            localStorage.setItem("searchQuestion", searchQuery);
            if (searchContentType.value != null) {
              localStorage.setItem("searchContentType", searchContentType.value);
            }

            const resultUrl = `/mall/ai-result`;

            if (window.parent !== window) {
              window.parent.location.href = resultUrl;
            } else {
              window.location.href = resultUrl;
            }
          } else {
            // 추가 정보가 필요한 경우 - firstPrompt 저장 및 추가 질문 표시
            if (!firstPrompt.value) {
              firstPrompt.value = searchQuery;
            }

            const additionalQuestion = resData.naturalLanguageAdditionalQuestion;
            if (additionalQuestion) {
              addMessage("bot", additionalQuestion);
            } else if (!group.value && !requiredPerson.value) {
              addMessage("bot", "대상 부서와 인원을 알려주시면 더 정확한 추천이 가능합니다.");
            } else if (!group.value) {
              addMessage("bot", "대상 부서를 알려주세요. (예: 유아부, 초등부, 중고등부 등)");
            } else if (requiredPerson.value == null) {
              addMessage("bot", "참여 인원을 알려주세요. (예: 학생 5명, 교사 3명)");
            }
            isLoading.value = false;
          }
        } else {
          addMessage("bot", "죄송합니다. 챗봇 서비스에 문제가 발생했습니다.");
          isLoading.value = false;
        }
      })
      .catch((error) => {
        console.error(error);
        addMessage("bot", "죄송합니다. 챗봇 서비스에 문제가 발생했습니다.");
        isLoading.value = false;
      });
  }
};

// 질문 예시 클릭 핸들러
const handleQuestionClick = (question) => {
  userInput.value = question;
  handleSendMessage();
};

// 추천 질문 목록 (10개)
const allQuestions = [
  "어린이 주일학교 콘텐츠 추천해주세요",
  "재미있게 성경공부를 하려면 어떻게 해야 할까요?",
  "교사 교육 프로그램 알려주세요?",
  "유아부 교육 자료 찾고 있어요?",
  "중고등부 수련회 프로그램 추천해주세요?",
  "새신자 교육용 콘텐츠가 필요합니다?",
  "구역예배 공과 콘텐츠 추천해주세요?",
  "여름성경학교 주제와 콘텐츠 알려주세요?",
  "절기 행사 자료는 어디서 찾나요?",
  "찬양대 악보와 음원 콘텐츠 찾고 싶어요?",
];

const displayedQuestions = ref([]);

const getRandomQuestions = () => {
  // 배열 복사 후 셔플
  const shuffled = [...allQuestions].sort(() => 0.5 - Math.random());
  // 앞에서 4개 선택
  displayedQuestions.value = shuffled.slice(0, 4);
};

onMounted(() => {
  getRandomQuestions();

  // URL 쿼리 파라미터(`?q=...`)로 초기 질문이 전달되면 자동 전송
  // toTopComponent / MainInquirySection 진입점에서 동일한 챗봇 흐름을 재사용하기 위함
  try {
    const params = new URLSearchParams(window.location.search);
    const initialQuery = params.get("q");
    if (initialQuery && initialQuery.trim()) {
      userInput.value = initialQuery.trim();
      handleSendMessage();
    }
  } catch (e) {
    // URL 파싱 실패는 무시하고 일반 진입 흐름 유지
  }
});
</script>
